import { jsx as _jsx } from "react/jsx-runtime";
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { RouteErrorBoundary } from './components/ErrorBoundary';
import { RouteTitle } from './lib/pageTitle';
import { marketingRoutes } from './marketingPages';
import { PATHS, ROUTES } from './routes';
function page(load, pick) {
    return async ()=>({
            Component: pick(await load())
        });
}
// Forwards :id to the canonical order detail route. Used by both the
// find-me-buys aliases and the unprefixed links older notifications sent out.
function LegacyOrderRedirect() {
    const { id } = useParams();
    return /*#__PURE__*/ _jsx(Navigate, {
        to: ROUTES.orderDetail(id ?? ''),
        replace: true
    });
}
// Each page's tab title: the deepest matched route's handle (or the page's own, see
// lib/pageTitle.tsx). Short names match the sidebar.
const t = (title)=>({
        title
    });
// Share-card renders for `ui-cli og-cards` (pages/OgCard). Dev builds only: production drops them.
const devOnly = process.env.NODE_ENV === 'development' ? [
    {
        path: '/__og',
        lazy: page(()=>import('./pages/OgCard/OgCard'), (m)=>m.OgCardIndex)
    },
    {
        path: '/__og/:slug',
        lazy: page(()=>import('./pages/OgCard/OgCard'), (m)=>m.OgCard)
    }
] : [];
const pages = [
    // The public marketing pages (prerendered at build time; see marketingPages.tsx).
    ...marketingRoutes,
    ...devOnly,
    {
        path: '/product/*',
        element: /*#__PURE__*/ _jsx(Navigate, {
            to: "/",
            replace: true
        })
    },
    {
        path: ROUTES.login,
        lazy: page(()=>import('./pages/Login'), (m)=>m.default),
        handle: t('Sign in')
    },
    // Public account pages: joining a shop from an invite, and resetting a password.
    {
        path: '/invite/:token',
        lazy: page(()=>import('./pages/Account/InviteAccept'), (m)=>m.default),
        handle: t('Join your shop')
    },
    // Self sign-up and its emailed confirmation link.
    {
        path: ROUTES.signup,
        lazy: page(()=>import('./pages/Account/SignUp'), (m)=>m.default),
        handle: t('Sign up')
    },
    {
        path: '/verify-email/:token',
        lazy: page(()=>import('./pages/Account/VerifyEmail'), (m)=>m.default),
        handle: t('Confirm your email')
    },
    {
        path: ROUTES.forgotPassword,
        lazy: page(()=>import('./pages/Account/ResetPassword'), (m)=>m.ForgotPassword),
        handle: t('Reset your password')
    },
    {
        path: '/reset-password/:token',
        lazy: page(()=>import('./pages/Account/ResetPassword'), (m)=>m.ResetPassword),
        handle: t('Choose a new password')
    },
    {
        lazy: page(()=>import('./components/ProtectedRoute'), (m)=>m.default),
        children: [
            // A shop that signed itself up pays here before anything else (outside the shell, so
            // nothing else loads).
            {
                path: ROUTES.payToStart,
                lazy: page(()=>import('./pages/PayToStart/PayToStart'), (m)=>m.default),
                handle: t('Pay to start')
            },
            {
                path: PATHS.app,
                lazy: page(()=>import('./components/Layout'), (m)=>m.default),
                // Any render error inside the app shell shows our own recoverable
                // screen instead of react-router's raw "Unexpected Application Error".
                errorElement: /*#__PURE__*/ _jsx(RouteErrorBoundary, {}),
                children: [
                    // Today is the landing screen; inventory keeps its own /app/vehicles route
                    {
                        index: true,
                        lazy: page(()=>import('./pages/Today/Today'), (m)=>m.Today),
                        handle: t('Today')
                    },
                    {
                        path: PATHS.today,
                        lazy: page(()=>import('./pages/Today/Today'), (m)=>m.Today),
                        handle: t('Today')
                    },
                    {
                        path: PATHS.inventory,
                        lazy: page(()=>import('./pages/Inventory/Inventory'), (m)=>m.Inventory),
                        handle: t('Cars')
                    },
                    {
                        path: PATHS.vehicleDetail,
                        lazy: page(()=>import('./pages/VehicleDetail/VehicleDetail'), (m)=>m.VehicleDetailPage),
                        handle: t('Car')
                    },
                    {
                        path: PATHS.automationBrowser,
                        lazy: page(()=>import('./pages/AutomationBrowser'), (m)=>m.AutomationBrowser),
                        handle: t('Automation browser')
                    },
                    {
                        path: PATHS.auctionBrowser,
                        lazy: page(()=>import('./pages/AuctionBrowser'), (m)=>m.AuctionBrowser),
                        handle: t('Shop browser')
                    },
                    // Customers
                    {
                        path: PATHS.customers,
                        lazy: page(()=>import('./pages/Customers/Customers'), (m)=>m.Customers),
                        handle: t('Customers')
                    },
                    {
                        path: PATHS.customerDetail,
                        lazy: page(()=>import('./pages/Customers/CustomerDetail'), (m)=>m.CustomerDetail),
                        handle: t('Customer')
                    },
                    // Legacy prospect/car routes now redirect into the unified vehicle UI
                    {
                        path: PATHS.carDetail,
                        lazy: page(()=>import('./pages/CarVinRedirect'), (m)=>m.default)
                    },
                    {
                        path: PATHS.prospects,
                        element: /*#__PURE__*/ _jsx(Navigate, {
                            to: ROUTES.inventory,
                            replace: true
                        })
                    },
                    {
                        path: PATHS.prospectDetail,
                        element: /*#__PURE__*/ _jsx(Navigate, {
                            to: ROUTES.inventory,
                            replace: true
                        })
                    },
                    {
                        path: PATHS.auction,
                        lazy: page(()=>import('./pages/AuctionSearch/AuctionSearch'), (m)=>m.AuctionSearch),
                        handle: t('Auction search')
                    },
                    {
                        path: PATHS.auctionVehicle,
                        lazy: page(()=>import('./pages/AuctionSearch/VehicleDetail'), (m)=>m.VehicleDetail),
                        handle: t('Auction lot')
                    },
                    // Shipping/importing was retired; documents live in the cost tracker
                    {
                        path: PATHS.importing,
                        element: /*#__PURE__*/ _jsx(Navigate, {
                            to: ROUTES.costTracker,
                            replace: true
                        })
                    },
                    {
                        path: PATHS.costTracker,
                        lazy: page(()=>import('./pages/CostTracker/CostTracker'), (m)=>m.CostTracker),
                        handle: t('Landed cost')
                    },
                    {
                        path: PATHS.shipments,
                        lazy: page(()=>import('./pages/Shipments/Shipments'), (m)=>m.Shipments),
                        handle: t('Shipments')
                    },
                    {
                        path: PATHS.customs,
                        lazy: page(()=>import('./pages/Customs/Customs'), (m)=>m.Customs),
                        handle: t('Customs')
                    },
                    {
                        path: PATHS.customsEntry,
                        lazy: page(()=>import('./pages/Customs/CustomsEntry'), (m)=>m.CustomsEntryPage),
                        handle: t('Customs entry')
                    },
                    {
                        path: PATHS.customsOnboarding,
                        lazy: page(()=>import('./pages/CustomsOnboarding/CustomsOnboarding'), (m)=>m.CustomsOnboardingPage),
                        handle: t('Customs onboarding')
                    },
                    {
                        path: PATHS.customsIsf,
                        lazy: page(()=>import('./pages/CustomsOnboarding/IsfForm'), (m)=>m.IsfFormPage),
                        handle: t('ISF form')
                    },
                    // Hunts (stored as custom orders). Find-me-buys runs are hunt runs now; the old
                    // standalone run page redirects to the hunts list.
                    {
                        path: PATHS.orders,
                        lazy: page(()=>import('./pages/FindMeBuys/FindMeBuysHistory'), (m)=>m.FindMeBuysHistory),
                        handle: t('Hunts')
                    },
                    {
                        path: PATHS.myShop,
                        lazy: page(()=>import('./pages/FindMeBuys/MyShop'), (m)=>m.MyShop),
                        handle: t('My shop')
                    },
                    {
                        path: PATHS.orderDetail,
                        lazy: page(()=>import('./pages/FindMeBuys/CustomOrderDetail'), (m)=>m.CustomOrderDetail),
                        handle: t('Hunt')
                    },
                    {
                        path: PATHS.findMeBuys,
                        element: /*#__PURE__*/ _jsx(Navigate, {
                            to: ROUTES.orders,
                            replace: true
                        })
                    },
                    {
                        path: PATHS.customOrderDetail,
                        element: /*#__PURE__*/ _jsx(LegacyOrderRedirect, {})
                    },
                    {
                        path: PATHS.findMeBuysResults,
                        element: /*#__PURE__*/ _jsx(Navigate, {
                            to: ROUTES.orders,
                            replace: true
                        })
                    },
                    // Outbox (customer email review). The full inbox page was removed —
                    // linked threads live on hunt / car / customer pages now.
                    {
                        path: PATHS.outbox,
                        lazy: page(()=>import('./pages/Outbox/Outbox'), (m)=>m.Outbox),
                        handle: t('Outbox')
                    },
                    {
                        path: PATHS.inbox,
                        element: /*#__PURE__*/ _jsx(Navigate, {
                            to: ROUTES.outbox,
                            replace: true
                        })
                    },
                    {
                        path: PATHS.inboxThread,
                        element: /*#__PURE__*/ _jsx(Navigate, {
                            to: ROUTES.outbox,
                            replace: true
                        })
                    },
                    // Every signed-in user manages their own settings
                    {
                        path: PATHS.settings,
                        lazy: page(()=>import('./pages/Settings'), (m)=>m.Settings),
                        handle: t('Settings')
                    },
                    {
                        path: PATHS.team,
                        lazy: page(()=>import('./pages/Team/Team'), (m)=>m.Team),
                        handle: t('Team')
                    },
                    {
                        path: PATHS.welcome,
                        lazy: page(()=>import('./pages/Welcome/Welcome'), (m)=>m.Welcome),
                        handle: t('Setup')
                    },
                    {
                        path: PATHS.help,
                        lazy: page(()=>import('./pages/Help/Help'), (m)=>m.Help),
                        handle: t('Help')
                    },
                    // Admin-only routes
                    {
                        lazy: page(()=>import('./components/AdminRoute'), (m)=>m.default),
                        children: [
                            {
                                path: PATHS.admin,
                                lazy: page(()=>import('./pages/Admin/AdminDashboard'), (m)=>m.AdminDashboard),
                                handle: t('Platform')
                            }
                        ]
                    }
                ]
            }
        ]
    },
    // Unprefixed legacy links from older notifications — the server's SPA
    // fallback serves index.html, so these land here instead of 404ing.
    {
        path: '/orders',
        element: /*#__PURE__*/ _jsx(Navigate, {
            to: ROUTES.orders,
            replace: true
        })
    },
    {
        path: '/orders/:id',
        element: /*#__PURE__*/ _jsx(LegacyOrderRedirect, {})
    },
    {
        path: '/find-me-buys',
        element: /*#__PURE__*/ _jsx(Navigate, {
            to: ROUTES.orders,
            replace: true
        })
    },
    {
        path: '/find-me-buys/orders/:id',
        element: /*#__PURE__*/ _jsx(LegacyOrderRedirect, {})
    },
    {
        path: '*',
        lazy: page(()=>import('./pages/NotFound'), (m)=>m.default),
        handle: t('Page not found')
    }
];
// One pathless root so RouteTitle sees every match; it renders no markup of its own, so
// prerendered marketing pages (rendered without it) still hydrate.
export const routes = [
    {
        element: /*#__PURE__*/ _jsx(RouteTitle, {}),
        children: pages
    }
];
const router = createBrowserRouter(routes);
export default router;
